/** EN catalog — FE-7: progress, journey, reassessment, family care continuity. */
export const progressEn: Record<string, string> = {
  'pg.title': 'Progress',
  'pg.sub':
    'What the record shows today — the current care stage, attendance counted per service, and the prose that carries the clinical judgment; every card states its own source.',
  'pg.subEmpty':
    'A fresh record — nothing is locked; the Initial Assessment is the recommended start.',
  'pg.subLimited':
    'The same page, shaped by access — what this role can’t see is named, and the rest of the page keeps working.',
  'pg.openTimeline': 'Open the longitudinal view',
  'pg.stageEyebrow': 'Current care stage',
  'pg.stageCaption':
    'States where the record stands right now — it changes as assessments, packages and sessions change, and it can move back.',
  'pg.stage.pre_registration': 'Registration not completed',
  'pg.stage.pre_registrationBody': 'A first contact without a completed record yet.',
  'pg.stage.registered': 'Registered',
  'pg.stage.registeredBody':
    'The record exists. Booking is open; the clinical story hasn’t started yet.',
  'pg.stage.initial_assessment': 'Assessment complete — awaiting package',
  'pg.stage.initial_assessmentBody':
    'A completed assessment is on file and no package has been created yet.',
  'pg.stage.package': 'Package in place',
  'pg.stage.packageBody': 'A package exists and no sessions have been delivered yet.',
  'pg.stage.treatment_delivery': 'Treatment delivery',
  'pg.stage.treatment_deliveryBody':
    'At least one session has been delivered and treatment is not yet complete.',
  'pg.stage.package_completion': 'Packages complete',
  'pg.stage.package_completionBody': 'Every current package is complete. No next step is required.',
  'pg.stage.reassessment_loop': 'Reassessed after completion',
  'pg.stage.reassessment_loopBody':
    'All packages are complete and a newer reassessment is signed — a next package can start from it.',
  'pg.stageChanged': 'Care stage updated — from {from} to {to}.',
  'pg.justNow': 'Just now',
  'pg.stagePresentTitle': 'The stage speaks in the present',
  'pg.stagePresentBody':
    'This note appears only because the change happened here a moment ago; a later visit simply shows the current stage.',
  'pg.countsCaption': 'Session counts only — never a measure of clinical change.',
  'pg.att.done': 'Complete',
  'pg.att.delivery': 'In delivery',
  'pg.att.stalled': 'No upcoming sessions',
  'pg.packageTotal': 'Package total',
  'pg.lastDelivered': 'Last delivered {date} · nothing scheduled ahead. Session counts only.',
  'pg.openScheduling': 'Open scheduling',
  'pg.viewPackage': 'View package',
  'pg.stalledTitle': 'No upcoming sessions are booked',
  'pg.stalledBody':
    'Sessions remain on an incomplete package and none are booked. That is a scheduling fact, not a clinical judgment — book the next session whenever the family is ready.',
  'pg.multiTitle': 'Completion needs every package',
  'pg.multiBody':
    'With a package still open, the record stays in Treatment delivery — a finished package is that package’s own fact.',
  'pg.emptyTitle': 'Nothing to read yet',
  'pg.emptyBody':
    'No packages, no assessments, no sessions. Booking stays free; the clinical story starts with the first assessment.',
  'pg.expected': 'Expected outcomes',
  'pg.expectedChip': 'Assessment prose',
  'pg.expectedNone': 'No expected outcomes were recorded for this assessment.',
  'pg.observation': 'Latest outcome observation',
  'pg.observationChip': 'Session note',
  'pg.observationNone': 'No outcome observation has been recorded yet.',
  'pg.familyGoals': 'Family context goals',
  'pg.familyGoalsChip': 'Intake · self-reported',
  'pg.familyGoalsChipStaff': 'Intake · staff-assisted',
  'pg.familyGoalsChipNone': 'Intake',
  'pg.familyGoalsNone': 'No family context goals were recorded at intake.',
  'pg.familyGoalsMeta': 'Intake · {date} · reported by the family',
  'pg.familyGoalsMetaStaff': 'Intake · {date} · recorded by clinic staff',
  'pg.readInContext': 'Read in context',
  'pg.limitedTitle': 'Clinical write-ups',
  'pg.limitedChip': 'Not in your access',
  'pg.limitedBody':
    'Assessment and session write-ups aren’t part of your role. Attendance, the care stage and scheduling stay fully available.',
  'pg.limitedAssessBody':
    'Assessment write-ups aren’t part of your role. The rest of this page, including session observations, stays available.',
  'pg.limitedSessionsBody':
    'Session write-ups aren’t part of your role. The rest of this page, including assessment prose, stays available.',
  'pg.completionStrip':
    'When a fresh clinical picture would help, a reassessment can begin — it is never required.',
  'pg.startReassessment': 'Start reassessment',
  'pg.nothingRequiredTitle': 'Nothing is required next',
  'pg.nothingRequiredBody':
    'Booking stays open and the record is in good standing. A reassessment or a new package are options, not obligations.',
  'pg.loopStrip':
    'A next package can start from the reassessment — an option, never a requirement.',
  'pg.loopCta': 'Start a package from the reassessment',
  'pg.fact1': 'All packages complete',
  'pg.fact1Body':
    'Every planned session on every current package is delivered — latest effective delivery {date}.',
  'pg.fact2': 'Reassessment signed after the latest delivery',
  'pg.fact2Body': 'Signed by {who} · {date}.',
  'pg.viewReassessment': 'View the reassessment',
  'pg.loopNoteTitle': 'A description of now, not a milestone',
  'pg.loopNoteBody':
    'This state simply describes the present: it steps aside when a new package starts and forces nothing — booking and assessing stay open.',
  'pg.factN': 'Fact {n, number}',

  'pg.tlTitle': 'Change over time',
  'pg.tlSub':
    'The record’s documents in time order — assessment prose, session observations and intake context side by side. Comparison and inference are the clinical reader’s judgment.',
  'pg.tlHead': 'The chronology',
  'pg.tlHeadSub': 'Chronology only — no formal comparison, no causal links.',
  'pg.tlEmpty': 'Nothing on the record yet.',
  'pg.tl.assessment_signed': '{type} · signed',
  'pg.tl.assessment_draft': '{type} · draft',
  'pg.tl.session_observation': 'Session observation',
  'pg.tl.session_delivered': 'Session delivered',
  'pg.tl.session_voided': 'Session · voided',
  'pg.tl.intake': 'Intake · self-reported',
  'pg.tlDelivered': 'Delivered — no outcome observation entered.',
  'pg.tlVoided': 'A delivery voided; the session and its notes remain on record.',
  'pg.tlIntake': 'The family’s context goals, from the intake.',
  'pg.tlDraft': 'A draft in progress.',
  'pg.tlSigned': 'A signed clinical picture.',
  'pg.tlComposed':
    'Composed from the record’s own documents; a single chronological read from the clinic system would replace this composition.',

  'as.startReassessment': 'Start reassessment',
  'as.coexistTitle': 'Assessments coexist',
  'as.coexistBody':
    'A reassessment needs no earlier assessment and stands on its own — earlier documents stay untouched.',
  'as.noPrereqTitle': 'No prerequisite, no link',
  'as.noPrereqBody':
    'A reassessment requires no prior initial assessment and stands on its own — earlier documents stay untouched.',
  'pk.reassessNoteTitle': 'The reassessment does not automatically change existing Packages',
  'pk.reassessNoteBody':
    'Existing packages are neither closed, extended nor re-priced — this new package is its own decision, and later amendments to the assessment never flow into it.',
  'rec.progressSub':
    'Current care stage, attendance per service, and the prose that carries the judgment.',
  'rec.openProgress': 'Open progress',
};
